import cronstrue from "cronstrue";
import {
  JobsConfigurationCommand,
  JobsConfigurationJob
} from "modules/system/sections/plt/field-management/rings/rollout-package/configure/jobs/dtos";
import { ReadableCronDisplay } from "modules/system/sections/plt/field-management/rings/rollout-package/configure/jobs/ReadableCronDisplay";

import { newGuid } from "@bps/common-utils";
import {
  CheckboxField,
  Stack,
  SubmissionForm,
  TextInputField,
  useTheme
} from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";
import { CustomRJSFSchema } from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { CommandNameInput } from "./CommandNameInput";
import { CommandSchemaForm } from "./CommandSchemaForm";

export const commandFormValidator = new Validator<JobsConfigurationCommand>();

interface CommandFormProps {
  job: JobsConfigurationJob;
  command?: JobsConfigurationCommand;
  schemas: CustomRJSFSchema[];
  isLoading: boolean;
  onUpdate: (
    command: JobsConfigurationCommand,
    job: JobsConfigurationJob
  ) => void;
  onDismiss: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
}

export const CommandForm: React.FC<CommandFormProps> = ({
  job,
  command,
  schemas,
  isLoading,
  onUpdate,
  onDismiss
}) => {
  const theme = useTheme();

  const commandFormSchema: ValidationSchema<JobsConfigurationCommand> = {
    commandName: [
      commandFormValidator.string().maxLength(255, {
        message: "Maximum length is 255 characters"
      }),
      commandFormValidator
        .string()
        .required({ message: "Command Name is required." })
    ],
    message: commandFormValidator
      .string()
      .required({ message: "Message is required." }),
    code: [
      commandFormValidator.custom().func<"code">((_value, values) => {
        if (job.commands.some(x => x.code === _value && x.id !== values.id)) {
          return `Code '${_value}' is already in use.`;
        } else return _value;
      }),
      commandFormValidator.string().required({ message: "Code is required." })
    ],
    uploadFrequency: [
      commandFormValidator.custom().func<"uploadFrequency">(value => {
        if (!value) return value;

        try {
          cronstrue.toString(value);
          return value;
        } catch {
          return "Invalid cron expression";
        }
      })
    ]
  };

  const initialValues =
    command ??
    ({
      id: newGuid()
    } as JobsConfigurationCommand);

  const save = async (values: JobsConfigurationCommand) => {
    onUpdate(values, job);
    onDismiss();
  };

  const dismiss = () => {
    onDismiss?.();
  };

  return (
    <SubmissionForm<JobsConfigurationCommand>
      onSubmit={save}
      cancelButtonProps={{ text: "Close", onClick: dismiss }}
      initialValues={initialValues}
      validate={values =>
        commandFormValidator.validate(values, commandFormSchema)
      }
    >
      {formState => {
        return (
          <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <TextInputField label="Code" required name="code" />

              <CommandNameInput
                values={schemas}
                isLoading={isLoading}
                styles={{ root: { width: "450px" } }}
              />
            </Stack>

            <CommandSchemaForm schemas={schemas} formState={formState} />

            <CheckboxField
              label="Run on start?"
              name="runOnStart"
              styles={{ root: { minWidth: "300px" } }}
            />
            <Stack
              horizontal
              horizontalAlign="space-between"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <Stack
                tokens={{ childrenGap: 4 }}
                styles={{ root: { minWidth: "450px" } }}
              >
                <TextInputField
                  label="Upload Frequency"
                  name="uploadFrequency"
                />
                <ReadableCronDisplay field="uploadFrequency" />
              </Stack>
              <TextInputField
                label="Upload Window"
                name="uploadWindow"
                placeholder="Duration e.g. 1:00:00"
              />
            </Stack>
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};
